import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import { IMAGE_CONSTANTS } from '../../constants/ImageConstans';
import {
  getUserNotifications,
  markAllNotificationsRead,
} from '../../network/network_callbacks';
import {
  extractNotificationsFromResponse,
  formatNotificationTime,
  groupNotificationsByDate,
  normalizeNotification,
} from '../../utils/notificationUtils';
import './NotificationList.css';

const PANEL_ANIMATION_MS = 350;

const NotificationListItem = ({ notification }) => {
  const { title, message, createdAt, image } = notification;
  const bellIcon =
    IMAGE_CONSTANTS.V2_NOTIFICATION_BELL_WAVE ||
    IMAGE_CONSTANTS.V2_REVAMPED_BELL_WAVE ||
    IMAGE_CONSTANTS.V2_REVAMPED_HEADER_BELL;
  const iconCircle = IMAGE_CONSTANTS.V2_NOTIFICATION_ICON_CIRCLE;
  const timeLabel = formatNotificationTime(createdAt);

  return (
    <article
      className="notification-list-item"
      aria-label={title}
      data-node-id="20246:17515"
    >
      <div className="notification-list-item__head" data-node-id="20246:17516">
        <div className="notification-list-item__title-row" data-node-id="20246:17517">
          <div className="notification-list-item__icon-wrap" data-node-id="20246:17518">
            {iconCircle ? (
              <img
                className="notification-list-item__icon-bg"
                src={iconCircle}
                alt=""
                data-node-id="20246:17519"
              />
            ) : null}
            {image ? (
              <img className="notification-list-item__icon" src={image} alt="" />
            ) : bellIcon ? (
              <img
                className="notification-list-item__icon"
                src={bellIcon}
                alt=""
                data-node-id="20246:17537"
              />
            ) : null}
          </div>
          <div className="notification-list-item__title-wrap" data-node-id="20246:17521">
            <h2 className="notification-list-item__title" data-node-id="20246:17539">
              {title}
            </h2>
          </div>
        </div>
        {timeLabel ? (
          <div className="notification-list-item__time-wrap" data-node-id="20246:17540">
            <time
              className="notification-list-item__time"
              dateTime={String(createdAt)}
              data-node-id="20246:17541"
            >
              {timeLabel}
            </time>
          </div>
        ) : null}
      </div>
      {message ? (
        <p className="notification-list-item__message" data-node-id="20246:17542">
          {message}
        </p>
      ) : null}
    </article>
  );
};

/**
 * Slide-in notification panel (right → left). Does not change the URL.
 */
export const NotificationPanel = ({ isOpen, onClose }) => {
  const [isRendered, setIsRendered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getUserNotifications();
      if (!response) {
        setError(TEXT_LABELS.NOTIFICATION_LIST_ERROR);
        setNotifications([]);
        return;
      }

      const { items, error: apiError } = extractNotificationsFromResponse(response);
      if (apiError) {
        setError(apiError);
        setNotifications([]);
        return;
      }

      const normalized = items
        .map((item) =>
          normalizeNotification(item, TEXT_LABELS.NOTIFICATION_LIST_TITLE)
        )
        .filter((item) => item.id || item.title);

      setNotifications(normalized);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError(TEXT_LABELS.NOTIFICATION_LIST_ERROR);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsActive(true));
      });
      return () => cancelAnimationFrame(frame);
    }

    setIsActive(false);
    const timer = window.setTimeout(() => setIsRendered(false), PANEL_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    loadNotifications();

    let cancelled = false;
    markAllNotificationsRead().catch((error) => {
      if (!cancelled) {
        console.error('Failed to mark notifications as read:', error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, loadNotifications]);

  useEffect(() => {
    if (!isRendered) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isRendered]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications]
  );

  if (!isRendered) return null;

  const overlay = (
    <div
      className={`notification-list-backdrop${isActive ? ' is-active' : ''}`}
      data-node-id="20246:17506"
      onClick={handleClose}
      role="presentation"
    >
      <section
        className={`notification-list-panel${isActive ? ' is-active' : ''}`}
        aria-label="Notifications"
        data-node-id="20246:17507"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="notification-list-panel__header">
          <div
            className="notification-list-panel__header-bar-wrap"
            data-node-id="20246:17508"
          >
            {IMAGE_CONSTANTS.V2_NOTIFICATION_HEADER_BAR ? (
              <img
                className="notification-list-panel__header-bar"
                src={IMAGE_CONSTANTS.V2_NOTIFICATION_HEADER_BAR}
                alt=""
                aria-hidden="true"
              />
            ) : null}
          </div>
          <h1 className="notification-list-panel__title">
            {TEXT_LABELS.NOTIFICATION_LIST_TITLE}
          </h1>
          <button
            type="button"
            className="notification-list-panel__close"
            aria-label="Close notifications"
            onClick={handleClose}
          >
            <img
              src={
                IMAGE_CONSTANTS.V2_NOTIFICATION_CLOSE_X ||
                IMAGE_CONSTANTS.V2_REVAMPED_CREATE_ROOM_CLOSE
              }
              alt=""
            />
          </button>
        </div>

        <div className="notification-list-panel__body">
          {loading ? (
            <div className="notification-list-panel__state">
              <p className="notification-list-panel__state-subtitle">
                {TEXT_LABELS.NOTIFICATION_LIST_LOADING}
              </p>
            </div>
          ) : error ? (
            <div className="notification-list-panel__state">
              <p className="notification-list-panel__state-title">{error}</p>
              <button
                type="button"
                className="notification-list-panel__retry"
                onClick={loadNotifications}
              >
                {TEXT_LABELS.NOTIFICATION_LIST_RETRY}
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-list-panel__state">
              <p className="notification-list-panel__state-title">
                {TEXT_LABELS.NOTIFICATION_LIST_EMPTY_TITLE}
              </p>
              <p className="notification-list-panel__state-subtitle">
                {TEXT_LABELS.NOTIFICATION_LIST_EMPTY_SUBTITLE}
              </p>
            </div>
          ) : (
            <div className="notification-list-panel__sections">
              {groupedNotifications.map((group) => (
                <section
                  key={group.label}
                  className="notification-list-panel__section"
                >
                  <div
                    className="notification-list-panel__section-label"
                    data-node-id="20246:17512"
                  >
                    <span data-node-id="20246:17513">{group.label}</span>
                  </div>
                  <div className="notification-list-panel__list">
                    {group.items.map((notification, index) => (
                      <NotificationListItem
                        key={notification.id || `${group.label}-${index}`}
                        notification={notification}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default NotificationPanel;
